import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const GA_ID = "G-1V9QNNM4JG";
const CONSENT_KEY = "cookie_consent";

type ConsentValue = "granted" | "denied";

function loadGA() {
  if (document.getElementById("ga-script")) return;

  const script = document.createElement("script");
  script.id = "ga-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  }
  gtag("js", new Date());
  gtag("config", GA_ID);
}

function revokeGA() {
  // Remove GA scripts
  document.getElementById("ga-script")?.remove();
  // Clear GA cookies
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    if (name.startsWith("_ga") || name.startsWith("_gid")) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  });
}

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

// Context so the footer (or anywhere) can reopen the banner
type CookieConsentContextType = { openBanner: () => void };
const CookieConsentContext = createContext<CookieConsentContextType>({ openBanner: () => {} });
export const useCookieConsent = () => useContext(CookieConsentContext);

const CookieConsent = ({ children }: { children?: React.ReactNode }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY) as ConsentValue | null;
    if (stored === "granted") {
      loadGA();
    } else if (stored === null) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const openBanner = useCallback(() => setVisible(true), []);

  const handleAccept = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "granted");
    setVisible(false);
    loadGA();
  }, []);

  const handleDecline = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "denied");
    setVisible(false);
    revokeGA();
  }, []);

  return (
    <CookieConsentContext.Provider value={{ openBanner }}>
      {children}
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-4 left-4 right-4 z-[9999] mx-auto max-w-lg rounded-lg border border-border bg-card p-5 shadow-2xl sm:left-auto sm:right-6 sm:bottom-6"
          >
            <button
              onClick={handleDecline}
              className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close cookie banner"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-start gap-3">
              <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="space-y-3">
                <p className="text-sm leading-relaxed text-foreground">
                  We use cookies to analyze site traffic and improve your experience.
                  See our{" "}
                  <Link to="/privacy" className="font-medium text-primary underline underline-offset-2 hover:text-primary/80">
                    Privacy Policy
                  </Link>{" "}
                  for details.
                </p>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleAccept}
                    className="rounded bg-primary px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    Accept
                  </button>
                  <button
                    onClick={handleDecline}
                    className="rounded border border-border px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  >
                    Decline
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </CookieConsentContext.Provider>
  );
};

export default CookieConsent;
